/**
 * Page de debug pour afficher les informations d'accès et de permissions
 * Accessible uniquement pour les administrateurs
 */

import { useAccessStore } from '@sphere/auth/data-access';
import { useIsAdmin } from '@sphere/auth/feature-security';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@sphere/shared/ui/design-system';
import { Tag, Button } from '@carbon/react';
import { Security, ArrowLeft, Renew } from '@carbon/icons-react';
import { useRefreshBootstrap } from '@sphere/auth/data-access';
import tw from './DebugAccessPage.tailwind.module.scss';

export default function DebugAccessPage() {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const { refetch, isLoading: isRefreshing } = useRefreshBootstrap();

  const user = useAccessStore((state: any) => state.user);
  const roles = useAccessStore((state: any) => state.roles);
  const sections = useAccessStore((state: any) => state.sections);
  const capabilities = useAccessStore((state: any) => state.capabilities);
  const scope = useAccessStore((state: any) => state.scope);
  const counts = useAccessStore((state: any) => state.counts);
  const partial = useAccessStore((state: any) => state.partial);
  const bootstrapLoaded = useAccessStore((state: any) => state.bootstrapLoaded);

  // Rediriger si pas admin
  if (!isAdmin && bootstrapLoaded) {
    navigate('/');
    return null;
  }

  const handleRefresh = async () => {
    await refetch();
  };

  return (
    <div className={`${tw.p6} ${tw.u1}`}>
      {/* Header */}
      <div className={tw.u2}>
        <div className={tw.u3}>
          <Button kind="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} className={tw.u4} />
            Retour
          </Button>
          <div className={tw.u5}>
            <Security size={24} className={tw.u6} />
            <h1 className={tw.u7}>Debug - Accès & Permissions</h1>
          </div>
        </div>
        <div className={tw.u5}>
          {partial && <Tag type="red">Mode dégradé</Tag>}
          <Button
            kind="tertiary"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <Renew
              size={16}
              className={`${tw.mr2} ${isRefreshing ? tw.animateSpin : ''}`}
            />
            Actualiser
          </Button>
        </div>
      </div>

      <div className={tw.u8}>
        {/* État du Bootstrap */}
        <Card>
          <CardHeader>
            <CardTitle className={tw.u9}>État du Bootstrap</CardTitle>
          </CardHeader>
          <CardContent className={tw.u10}>
            <div className={tw.u11}>
              <span className={tw.u12}>Chargé</span>
              <Tag type={bootstrapLoaded ? 'blue' : 'gray'}>
                {bootstrapLoaded ? 'Oui' : 'Non'}
              </Tag>
            </div>
            <div className={tw.u11}>
              <span className={tw.u12}>
                Mode dégradé
              </span>
              <Tag type={partial ? 'red' : 'blue'}>
                {partial ? 'Oui' : 'Non'}
              </Tag>
            </div>
            <div className={tw.u11}>
              <span className={tw.u12}>
                Dernière mise à jour
              </span>
              <span className={tw.u13}>
                {new Date().toLocaleTimeString('fr-FR')}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Informations utilisateur */}
        <Card>
          <CardHeader>
            <CardTitle className={tw.u9}>Utilisateur</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`${tw.p4} ${tw.u14}`}>
              <pre className={tw.u15}>
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Rôles */}
        <Card>
          <CardHeader>
            <CardTitle className={tw.u9}>
              Rôles
              <Tag type="outline" className={tw.u16}>
                {roles.length}
              </Tag>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {roles.length > 0 ? (
              <div className={tw.u17}>
                {roles.map((role: string) => (
                  <Tag type="blue" key={role}>
                    {role}
                  </Tag>
                ))}
              </div>
            ) : (
              <p className={tw.u12}>Aucun rôle défini</p>
            )}
          </CardContent>
        </Card>

        {/* Scope utilisateur */}
        <Card>
          <CardHeader>
            <CardTitle className={tw.u9}>Scope Utilisateur</CardTitle>
          </CardHeader>
          <CardContent className={tw.u10}>
            <div>
              <div className={tw.u18}>
                <span className={tw.u19}>Établissements</span>
                {scope.establishmentIds?.length ? (
                  <Tag type="outline">
                    {scope.establishmentIds.length}
                  </Tag>
                ) : (
                  <Tag type="gray">Aucun</Tag>
                )}
              </div>
              {scope.establishmentIds && scope.establishmentIds.length > 0 && (
                <div className={`${tw.p2} ${tw.u20}`}>
                  {scope.establishmentIds.join(', ')}
                </div>
              )}
            </div>
            <hr className={`cds--divider ${tw.u21}`} />
            <div>
              <div className={tw.u18}>
                <span className={tw.u19}>Classes</span>
                {scope.classIds?.length ? (
                  <Tag type="outline">{scope.classIds.length}</Tag>
                ) : (
                  <Tag type="gray">Aucune</Tag>
                )}
              </div>
              {scope.classIds && scope.classIds.length > 0 && (
                <div className={`${tw.p2} ${tw.u20}`}>
                  {scope.classIds.join(', ')}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sections visibles */}
        <Card className={tw.u22}>
          <CardHeader>
            <CardTitle className={tw.u9}>
              Sections Visibles
              <Tag type="outline" className={tw.u16}>
                {Object.keys(sections).length}
              </Tag>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(sections).length > 0 ? (
              <div className={tw.u23}>
                {Object.entries(sections).map(
                  ([key, section]: [string, any]) => (
                    <div
                      key={key}
                      className={`${tw.p3} ${tw.u24}`}
                    >
                      <span className={tw.u25}>{key}</span>
                      <Tag type={section.visible ? 'blue' : 'gray'}>
                        {section.visible ? 'Visible' : 'Masquée'}
                      </Tag>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className={tw.u12}>
                Aucune section définie
              </p>
            )}
          </CardContent>
        </Card>

        {/* Capabilities globales */}
        <Card className={tw.u22}>
          <CardHeader>
            <CardTitle className={tw.u9}>
              Capabilities Globales
              <Tag type="outline" className={tw.u16}>
                {Object.keys(capabilities).length}
              </Tag>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(capabilities).length > 0 ? (
              <div className={tw.u26}>
                {Object.entries(capabilities).map(
                  ([key, cap]: [string, any]) => (
                    <div key={key} className={`${tw.p3} ${tw.u27}`}>
                      <div className={tw.u28}>
                        {key}
                      </div>
                      <div className={tw.u29}>
                        <Tag
                          type={cap.list ? 'blue' : 'gray'}
                          className={tw.u30}
                        >
                          List: {cap.list ? '✓' : '✗'}
                        </Tag>
                        <Tag
                          type={cap.create ? 'blue' : 'gray'}
                          className={tw.u30}
                        >
                          Create: {cap.create ? '✓' : '✗'}
                        </Tag>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <p className={tw.u12}>
                Aucune capability définie
              </p>
            )}
          </CardContent>
        </Card>

        {/* Compteurs */}
        <Card className={tw.u22}>
          <CardHeader>
            <CardTitle className={tw.u9}>
              Compteurs
              <Tag type="outline" className={tw.u16}>
                {Object.keys(counts).length}
              </Tag>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(counts).length > 0 ? (
              <div className={tw.u31}>
                {Object.entries(counts).map(([key, count]: [string, any]) => (
                  <div
                    key={key}
                    className={`${tw.p3} ${tw.u24}`}
                  >
                    <span className={tw.u32} title={key}>
                      {key}
                    </span>
                    <Tag type="outline">{count}</Tag>
                  </div>
                ))}
              </div>
            ) : (
              <p className={tw.u12}>
                Aucun compteur défini
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
